(() => {
  if (customElements.get("cinematic-slider-portable")) return;

  const FOCUSABLE_SELECTOR = [
    "a[href]",
    "button:not([disabled])",
    "textarea:not([disabled])",
    "input:not([disabled])",
    "select:not([disabled])",
    "[tabindex]:not([tabindex='-1'])",
  ].join(",");

  const getCartAddUrl = () => window.routes?.cart_add_url || "/cart/add.js";

  const getFetchConfig = () => {
    if (typeof window.fetchConfig === "function") {
      const config = window.fetchConfig("javascript");
      config.headers["X-Requested-With"] = "XMLHttpRequest";
      delete config.headers["Content-Type"];
      return config;
    }

    return {
      method: "POST",
      headers: {
        Accept: "application/json",
        "X-Requested-With": "XMLHttpRequest",
      },
    };
  };

  class CinematicSliderPortable extends HTMLElement {
    constructor() {
      super();

      /*
      ========================================================
      STATE
      ========================================================
      */

      this.activeIndex = 0;
      this.activePhysicalIndex = 0;
      this.activeItem = null;

      this.items = [];
      this.originalItems = [];
      this.videos = [];
      this.itemData = [];

      this.originalCount = 0;
      this.loopEnabled = false;

      this.enableVideoAutoplay = true;

      this.isSnapping = false;
      this.isNormalizing = false;

      this.snapTimeout = null;
      this.loopEndTimeout = null;
      this.rafId = null;

      this.currentOpener = null;

      this.drag = {
        active: false,
        pointerId: null,
        startX: 0,
        startScrollLeft: 0,
        moved: false,
      };

      /*
      ========================================================
      BINDINGS
      ========================================================
      */

      this.onClick = this.onClick.bind(this);
      this.onKeyDown = this.onKeyDown.bind(this);
      this.onScroll = this.onScroll.bind(this);
      this.onResize = this.onResize.bind(this);

      this.onPointerDown = this.onPointerDown.bind(this);
      this.onPointerMove = this.onPointerMove.bind(this);
      this.onPointerUp = this.onPointerUp.bind(this);
    }

    /*
    ==========================================================
    INIT
    ==========================================================
    */

    connectedCallback() {
      if (this.hasAttribute("data-cinematic-ready")) return;

      this.setAttribute("data-cinematic-ready", "true");

      if (!this.hasAttribute("dir")) {
        this.setAttribute("dir", "ltr");
      }

      this.track = this.querySelector("[data-cinematic-track]");
      this.prevButton = this.querySelector("[data-cinematic-prev]");
      this.nextButton = this.querySelector("[data-cinematic-next]");

      if (!this.track) return;

      /*
      --------------------------------------------------------
      ORIGINAL ITEMS
      --------------------------------------------------------
      */

      this.originalItems = Array.from(
        this.track.querySelectorAll("[data-cinematic-item]")
      );

      this.originalCount = this.originalItems.length;

      if (!this.originalCount) return;

      /*
      --------------------------------------------------------
      LOOP
      --------------------------------------------------------
      */

      this.loopEnabled = this.originalCount > 1;

      this.setAttribute(
        "data-cinematic-loop",
        this.loopEnabled ? "true" : "false"
      );

      /*
      --------------------------------------------------------
      AUTOPLAY
      --------------------------------------------------------
      */

      this.enableVideoAutoplay =
        this.dataset.enableVideoAutoplay === "true";

      /*
      --------------------------------------------------------
      INITIAL VISUAL STATE
      --------------------------------------------------------
      */

      this.classList.add("is-cinematic-initializing");

      /*
      --------------------------------------------------------
      CREATE LOOP
      --------------------------------------------------------
      */

      if (this.loopEnabled) {
        this.createLoopClones();
      }

      /*
      --------------------------------------------------------
      CACHE
      --------------------------------------------------------
      */

      this.refreshCollections();

      /*
      --------------------------------------------------------
      EVENTS
      --------------------------------------------------------
      */

      this.bindEvents();

      /*
      --------------------------------------------------------
      START IN ORIGINAL CENTER SET
      --------------------------------------------------------
      */

      requestAnimationFrame(() => {
        this.cacheItems();

        const firstOriginal = this.items.find((item) => {
          return (
            !item.hasAttribute("data-cinematic-clone") &&
            Number(item.dataset.index) === 0
          );
        });

        const initialPhysicalIndex = Math.max(
          0,
          this.items.indexOf(firstOriginal)
        );

        this.setActivePhysical(initialPhysicalIndex);

        this.scrollToPhysicalIndex(
          initialPhysicalIndex,
          false,
          false
        );

        requestAnimationFrame(() => {
          this.classList.remove("is-cinematic-initializing");
        });
      });
    }

    disconnectedCallback() {
      this.removeEventListener("click", this.onClick);

      document.removeEventListener(
        "keydown",
        this.onKeyDown,
        true
      );

      window.removeEventListener(
        "resize",
        this.onResize
      );

      this.track?.removeEventListener(
        "scroll",
        this.onScroll
      );

      this.track?.removeEventListener(
        "pointerdown",
        this.onPointerDown
      );

      this.track?.removeEventListener(
        "pointermove",
        this.onPointerMove
      );

      this.track?.removeEventListener(
        "pointerup",
        this.onPointerUp
      );

      this.track?.removeEventListener(
        "pointercancel",
        this.onPointerUp
      );

      clearTimeout(this.snapTimeout);
      clearTimeout(this.loopEndTimeout);

      cancelAnimationFrame(this.rafId);

      /*
      --------------------------------------------------------
      REMOVE CLONES IF SHOPIFY RELOADS SECTION
      --------------------------------------------------------
      */

      this.track
        ?.querySelectorAll("[data-cinematic-clone]")
        .forEach((clone) => clone.remove());

      this.removeAttribute("data-cinematic-ready");
      this.removeAttribute("data-cinematic-loop");
    }

    /*
    ==========================================================
    LOOP CREATION
    ==========================================================
    */

    createLoopClones() {
      if (!this.track || this.originalCount <= 1) return;

      const beforeFragment =
        document.createDocumentFragment();

      const afterFragment =
        document.createDocumentFragment();

      /*
      --------------------------------------------------------
      BEFORE SET
      --------------------------------------------------------
      */

      this.originalItems.forEach((original, index) => {
        const clone = this.cloneSlide(
          original,
          "before",
          index
        );

        beforeFragment.appendChild(clone);
      });

      /*
      --------------------------------------------------------
      AFTER SET
      --------------------------------------------------------
      */

      this.originalItems.forEach((original, index) => {
        const clone = this.cloneSlide(
          original,
          "after",
          index
        );

        afterFragment.appendChild(clone);
      });

      /*
      DOM:
      
      CLONES BEFORE
      ORIGINALS
      CLONES AFTER
      */

      this.track.insertBefore(
        beforeFragment,
        this.originalItems[0]
      );

      this.track.appendChild(afterFragment);
    }

    cloneSlide(original, zone, cloneIndex) {
      const clone = original.cloneNode(true);

      clone.setAttribute(
        "data-cinematic-clone",
        zone
      );

      clone.setAttribute(
        "data-active",
        "false"
      );

      clone.setAttribute(
        "aria-current",
        "false"
      );

      clone.setAttribute(
        "tabindex",
        "-1"
      );

      /*
      --------------------------------------------------------
      REMOVE SHOPIFY EDITOR ATTRIBUTES FROM CLONES
      --------------------------------------------------------
      */

      const allElements = [
        clone,
        ...clone.querySelectorAll("*"),
      ];

      allElements.forEach((element) => {
        Array.from(element.attributes).forEach((attribute) => {
          if (
            attribute.name.startsWith("data-shopify")
          ) {
            element.removeAttribute(attribute.name);
          }
        });
      });

      /*
      --------------------------------------------------------
      UNIQUE IDs
      --------------------------------------------------------
      */

      const suffix =
        `--cinematic-${zone}-${cloneIndex}`;

      const idMap = new Map();

      const elementsWithId = [
        ...(clone.id ? [clone] : []),
        ...clone.querySelectorAll("[id]"),
      ];

      elementsWithId.forEach((element) => {
        const oldId = element.id;

        if (!oldId) return;

        const newId = `${oldId}${suffix}`;

        idMap.set(oldId, newId);

        element.id = newId;
      });

      /*
      --------------------------------------------------------
      UPDATE ARIA REFERENCES
      --------------------------------------------------------
      */

      const referenceAttributes = [
        "aria-controls",
        "aria-labelledby",
        "aria-describedby",
        "for",
      ];

      allElements.forEach((element) => {
        referenceAttributes.forEach((attribute) => {
          if (!element.hasAttribute(attribute)) return;

          const value =
            element.getAttribute(attribute);

          if (!value) return;

          const newValue = value
            .split(/\s+/)
            .map((token) => idMap.get(token) || token)
            .join(" ");

          element.setAttribute(
            attribute,
            newValue
          );
        });
      });

      /*
      --------------------------------------------------------
      CLOSE CLONED POPOVERS
      --------------------------------------------------------
      */

      clone
        .querySelectorAll("[data-cinematic-popover]")
        .forEach((popover) => {
          popover.hidden = true;
        });

      clone
        .querySelectorAll("[data-cinematic-toggle]")
        .forEach((toggle) => {
          toggle.setAttribute(
            "aria-expanded",
            "false"
          );
        });

      /*
      --------------------------------------------------------
      VIDEO PERFORMANCE
      --------------------------------------------------------
      */

      clone
        .querySelectorAll("[data-cinematic-video]")
        .forEach((video) => {
          video.muted = true;

          /*
          Clones don't preload aggressively.
          Browser loads once they become relevant.
          */

          video.setAttribute(
            "preload",
            "metadata"
          );

          try {
            video.pause();
            video.currentTime = 0;
          } catch (error) {}
        });

      return clone;
    }

    /*
    ==========================================================
    COLLECTION CACHE
    ==========================================================
    */

    refreshCollections() {
      if (!this.track) return;

      this.items = Array.from(
        this.track.querySelectorAll(
          "[data-cinematic-item]"
        )
      );

      this.videos = Array.from(
        this.track.querySelectorAll(
          "[data-cinematic-video]"
        )
      );

      this.cacheItems();
    }

    cacheItems() {
      this.itemData = this.items.map((item) => ({
        el: item,
        center:
          item.offsetLeft +
          item.offsetWidth / 2,
      }));
    }

    /*
    ==========================================================
    EVENTS
    ==========================================================
    */

    bindEvents() {
      this.addEventListener(
        "click",
        this.onClick
      );

      document.addEventListener(
        "keydown",
        this.onKeyDown,
        true
      );

      window.addEventListener(
        "resize",
        this.onResize,
        { passive: true }
      );

      this.track.addEventListener(
        "scroll",
        this.onScroll,
        { passive: true }
      );

      this.track.addEventListener(
        "pointerdown",
        this.onPointerDown
      );

      this.track.addEventListener(
        "pointermove",
        this.onPointerMove
      );

      this.track.addEventListener(
        "pointerup",
        this.onPointerUp
      );

      this.track.addEventListener(
        "pointercancel",
        this.onPointerUp
      );
    }

    onResize() {
      this.cacheItems();

      this.requestActiveUpdate();

      clearTimeout(this.loopEndTimeout);

      this.loopEndTimeout = setTimeout(() => {
        if (!this.activeItem) return;

        const physicalIndex =
          this.items.indexOf(
            this.activeItem
          );

        if (physicalIndex < 0) return;

        this.scrollToPhysicalIndex(
          physicalIndex,
          false,
          true
        );
      }, 80);
    }

    onScroll() {
      if (this.isNormalizing) return;

      this.requestActiveUpdate();

      clearTimeout(this.loopEndTimeout);

      this.loopEndTimeout = setTimeout(() => {
        if (
          this.drag.active ||
          this.isSnapping ||
          this.isNormalizing
        ) {
          return;
        }

        this.normalizeLoopPosition();
      }, 110);
    }

    requestActiveUpdate() {
      if (this.rafId) return;

      this.rafId = requestAnimationFrame(() => {
        if (
          !this.isSnapping &&
          !this.isNormalizing
        ) {
          this.updateActive();
        }

        this.rafId = null;
      });
    }

    /*
    ==========================================================
    ACTIVE SLIDE
    ==========================================================
    */

    updateActive() {
      if (!this.itemData.length) return;

      const viewportCenter =
        this.track.scrollLeft +
        this.track.clientWidth / 2;

      let closestIndex = 0;
      let minDistance = Infinity;

      this.itemData.forEach((item, index) => {
        const distance =
          Math.abs(
            item.center -
              viewportCenter
          );

        if (distance < minDistance) {
          minDistance = distance;
          closestIndex = index;
        }
      });

      this.setActivePhysical(
        closestIndex
      );
    }

    setActivePhysical(physicalIndex) {
      physicalIndex = Math.max(
        0,
        Math.min(
          physicalIndex,
          this.items.length - 1
        )
      );

      const activeItem =
        this.items[physicalIndex];

      if (!activeItem) return;

      const previousItem =
        this.activeItem;

      if (
        previousItem &&
        previousItem !== activeItem
      ) {
        this.closeAllPopovers();
      }

      this.items.forEach(
        (item, itemIndex) => {
          const isActive =
            itemIndex === physicalIndex;

          item.setAttribute(
            "data-active",
            isActive ? "true" : "false"
          );

          item.setAttribute(
            "aria-current",
            isActive ? "true" : "false"
          );

          item.setAttribute(
            "tabindex",
            isActive ? "0" : "-1"
          );

          const toggle =
            item.querySelector(
              "[data-cinematic-toggle]"
            );

          if (toggle) {
            toggle.disabled = false;

            toggle.setAttribute(
              "aria-disabled",
              "false"
            );
          }

          const playButton =
            item.querySelector(
              "[data-cinematic-play]"
            );

          if (playButton) {
            playButton.disabled =
              !isActive;

            playButton.setAttribute(
              "aria-disabled",
              isActive
                ? "false"
                : "true"
            );

            playButton.hidden =
              !isActive;
          }
        }
      );

      this.activePhysicalIndex =
        physicalIndex;

      this.activeItem =
        activeItem;

      const logicalIndex =
        Number(
          activeItem.dataset.index
        );

      this.activeIndex =
        Number.isNaN(logicalIndex)
          ? 0
          : logicalIndex;

      /*
      --------------------------------------------------------
      AUTOPLAY ONLY THE ACTUAL ACTIVE DOM SLIDE
      --------------------------------------------------------
      */

      if (this.enableVideoAutoplay) {
        const activeVideo =
          activeItem.querySelector(
            "[data-cinematic-video]"
          );

        this.videos.forEach((video) => {
          if (
            video === activeVideo
          ) {
            this.playVideo(video);
          } else {
            this.pauseVideo(
              video,
              true
            );
          }
        });
      } else {
        this.videos.forEach(
          (video) => {
            this.pauseVideo(
              video,
              true
            );
          }
        );
      }

      this.syncMuteButtons();
      this.updateButtons();
    }

    /*
    ==========================================================
    LOOP NORMALIZATION
    ==========================================================
    */

    normalizeLoopPosition() {
      if (
        !this.loopEnabled ||
        this.isNormalizing ||
        this.drag.active
      ) {
        return false;
      }

      const count =
        this.originalCount;

      const currentIndex =
        this.activePhysicalIndex;

      let targetIndex = null;

      /*
      DOM structure:
      
      0 ............... count - 1
      BEFORE CLONES

      count ........... 2count - 1
      ORIGINALS

      2count .......... 3count - 1
      AFTER CLONES
      */

      if (currentIndex < count) {
        targetIndex =
          currentIndex + count;
      } else if (
        currentIndex >= count * 2
      ) {
        targetIndex =
          currentIndex - count;
      }

      if (targetIndex === null) {
        return false;
      }

      const currentData =
        this.itemData[currentIndex];

      const targetData =
        this.itemData[targetIndex];

      if (
        !currentData ||
        !targetData
      ) {
        return false;
      }

      this.isNormalizing = true;

      const delta =
        targetData.center -
        currentData.center;

      /*
      Disable snap for one frame.
      */

      this.track.style.scrollSnapType =
        "none";

      /*
      Move to the identical slide
      in the original middle set.

      Since content is identical,
      the user cannot see this jump.
      */

      this.track.scrollLeft += delta;

      this.setActivePhysical(
        targetIndex
      );

      this.cacheItems();

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this.track.style.scrollSnapType =
            "";

          this.isNormalizing = false;
        });
      });

      return true;
    }

    /*
    ==========================================================
    NAVIGATION
    ==========================================================
    */

    updateButtons() {
      if (this.loopEnabled) {
        if (this.prevButton) {
          this.prevButton.disabled =
            false;
        }

        if (this.nextButton) {
          this.nextButton.disabled =
            false;
        }

        return;
      }

      if (this.prevButton) {
        this.prevButton.disabled =
          this.activePhysicalIndex <= 0;
      }

      if (this.nextButton) {
        this.nextButton.disabled =
          this.activePhysicalIndex >=
          this.items.length - 1;
      }
    }

    goBy(direction) {
      if (!this.items.length) return;

      /*
      Keep navigation centered
      inside the original set whenever possible.
      */

      if (this.loopEnabled) {
        this.normalizeLoopPosition();
      }

      let target =
        this.activePhysicalIndex +
        direction;

      if (!this.loopEnabled) {
        target = Math.max(
          0,
          Math.min(
            target,
            this.items.length - 1
          )
        );
      }

      if (
        target < 0 ||
        target >= this.items.length
      ) {
        return;
      }

      this.scrollToPhysicalIndex(
        target,
        true,
        true
      );
    }

    scrollToPhysicalIndex(
      physicalIndex,
      smooth = true,
      normalizeAfter = true
    ) {
      physicalIndex = Math.max(
        0,
        Math.min(
          physicalIndex,
          this.items.length - 1
        )
      );

      const item =
        this.items[physicalIndex];

      if (!item) return;

      this.cacheItems();

      const offset =
        item.offsetLeft -
        (
          this.track.clientWidth -
          item.offsetWidth
        ) /
          2;

      if (smooth) {
        this.track.style.scrollSnapType =
          "none";
      }

      this.isSnapping =
        smooth;

      this.setActivePhysical(
        physicalIndex
      );

      this.track.scrollTo({
        left: offset,
        behavior: smooth
          ? "smooth"
          : "auto",
      });

      clearTimeout(
        this.snapTimeout
      );

      this.snapTimeout = setTimeout(
        () => {
          this.isSnapping = false;

          this.track.style.scrollSnapType =
            "";

          this.cacheItems();

          this.updateActive();

          if (normalizeAfter) {
            this.normalizeLoopPosition();
          }
        },
        smooth ? 430 : 20
      );
    }

    /*
    ==========================================================
    DRAG
    ==========================================================
    */

    onPointerDown(event) {
      if (event.button !== 0) return;

      if (
        event.target.closest(
          FOCUSABLE_SELECTOR
        )
      ) {
        return;
      }

      this.drag.active = true;
      this.drag.pointerId =
        event.pointerId;

      this.drag.startX =
        event.clientX;

      this.drag.startScrollLeft =
        this.track.scrollLeft;

      this.drag.moved = false;

      this.track.classList.add(
        "is-dragging"
      );

      this.track.setPointerCapture?.(
        event.pointerId
      );
    }

    onPointerMove(event) {
      if (
        !this.drag.active ||
        event.pointerId !==
          this.drag.pointerId
      ) {
        return;
      }

      const delta =
        event.clientX -
        this.drag.startX;

      if (
        Math.abs(delta) > 5
      ) {
        this.drag.moved = true;
      }

      this.track.scrollLeft =
        this.drag.startScrollLeft -
        delta;
    }

    onPointerUp(event) {
      if (
        !this.drag.active ||
        event.pointerId !==
          this.drag.pointerId
      ) {
        return;
      }

      this.drag.active = false;

      this.track.classList.remove(
        "is-dragging"
      );

      this.track.releasePointerCapture?.(
        event.pointerId
      );

      if (this.drag.moved) {
        this.updateActive();

        window.setTimeout(() => {
          this.scrollToPhysicalIndex(
            this.activePhysicalIndex,
            true,
            true
          );
        }, 20);
      }
    }

    /*
    ==========================================================
    CLICK
    ==========================================================
    */

    onClick(event) {
      if (this.drag.moved) {
        this.drag.moved = false;
        return;
      }

      /*
      --------------------------------------------------------
      PREVIOUS
      --------------------------------------------------------
      */

      const prev =
        event.target.closest(
          "[data-cinematic-prev]"
        );

      if (prev) {
        event.preventDefault();

        this.goBy(-1);

        return;
      }

      /*
      --------------------------------------------------------
      NEXT
      --------------------------------------------------------
      */

      const next =
        event.target.closest(
          "[data-cinematic-next]"
        );

      if (next) {
        event.preventDefault();

        this.goBy(1);

        return;
      }

      /*
      --------------------------------------------------------
      PLAY
      --------------------------------------------------------
      */

      const play =
        event.target.closest(
          "[data-cinematic-play]"
        );

      if (play) {
        event.preventDefault();

        this.toggleVideo(play);

        return;
      }

      /*
      --------------------------------------------------------
      MUTE
      --------------------------------------------------------
      */

      const mute =
        event.target.closest(
          "[data-cinematic-mute]"
        );

      if (mute) {
        event.preventDefault();

        this.toggleMute(mute);

        return;
      }

      /*
      --------------------------------------------------------
      PRODUCT POPOVER
      --------------------------------------------------------
      */

      const toggle =
        event.target.closest(
          "[data-cinematic-toggle]"
        );

      if (toggle) {
        event.preventDefault();

        const item =
          toggle.closest(
            "[data-cinematic-item]"
          );

        if (!item) return;

        const physicalIndex =
          this.items.indexOf(item);

        const logicalIndex =
          Number(item.dataset.index);

        /*
        If user clicks an inactive side card,
        center it first.
        */

        if (
          physicalIndex !==
          this.activePhysicalIndex
        ) {
          this.scrollToPhysicalIndex(
            physicalIndex,
            true,
            true
          );

          window.setTimeout(() => {
            /*
            If we landed in a clone,
            move invisibly to original.
            */

            this.normalizeLoopPosition();

            const activeToggle =
              this.activeItem?.querySelector(
                "[data-cinematic-toggle]"
              );

            if (!activeToggle) return;

            const activeLogical =
              Number(
                this.activeItem.dataset.index
              );

            if (
              Number.isNaN(logicalIndex) ||
              activeLogical ===
                logicalIndex
            ) {
              this.openPopover(
                activeToggle
              );
            }
          }, 450);

          return;
        }

        /*
        If active item happens to be a clone,
        normalize it before opening.
        */

        if (
          item.hasAttribute(
            "data-cinematic-clone"
          )
        ) {
          this.normalizeLoopPosition();

          const activeToggle =
            this.activeItem?.querySelector(
              "[data-cinematic-toggle]"
            );

          if (activeToggle) {
            this.togglePopover(
              activeToggle
            );
          }

          return;
        }

        this.togglePopover(toggle);

        return;
      }

      /*
      --------------------------------------------------------
      CLOSE POPOVER
      --------------------------------------------------------
      */

      const close =
        event.target.closest(
          "[data-cinematic-close]"
        );

      if (close) {
        event.preventDefault();

        const item =
          close.closest(
            "[data-cinematic-item]"
          );

        const opener =
          item?.querySelector(
            "[data-cinematic-toggle]"
          );

        this.closePopover(
          opener
        );

        return;
      }

      /*
      --------------------------------------------------------
      ADD TO CART
      --------------------------------------------------------
      */

      const add =
        event.target.closest(
          "[data-cinematic-add]"
        );

      if (add) {
        event.preventDefault();

        this.handleAddToCart(add);
      }
    }

    /*
    ==========================================================
    KEYBOARD
    ==========================================================
    */

    onKeyDown(event) {
      if (
        event.key === "Escape" &&
        this.currentOpener
      ) {
        event.preventDefault();

        this.closePopover(
          this.currentOpener
        );

        return;
      }

      if (
        !this.contains(
          document.activeElement
        )
      ) {
        return;
      }

      if (
        event.key !== "ArrowLeft" &&
        event.key !== "ArrowRight"
      ) {
        return;
      }

      const tag =
        document.activeElement?.tagName;

      if (
        tag === "INPUT" ||
        tag === "TEXTAREA" ||
        document.activeElement
          ?.isContentEditable
      ) {
        return;
      }

      event.preventDefault();

      this.goBy(
        event.key ===
          "ArrowLeft"
          ? -1
          : 1
      );

      requestAnimationFrame(() => {
        this.activeItem?.focus({
          preventScroll: true,
        });
      });
    }

    /*
    ==========================================================
    VIDEO
    ==========================================================
    */

    playVideo(video) {
      if (!video) return;

      const promise =
        video.play();

      if (promise?.catch) {
        promise.catch(() => {});
      }

      video
        .closest(
          "[data-cinematic-item]"
        )
        ?.querySelector(
          "[data-cinematic-play]"
        )
        ?.setAttribute(
          "aria-pressed",
          "true"
        );
    }

    pauseVideo(
      video,
      reset = false
    ) {
      if (!video) return;

      video.pause();

      if (reset) {
        try {
          video.currentTime = 0;
        } catch (error) {}
      }

      video
        .closest(
          "[data-cinematic-item]"
        )
        ?.querySelector(
          "[data-cinematic-play]"
        )
        ?.setAttribute(
          "aria-pressed",
          "false"
        );

      video.muted = true;
    }

    toggleVideo(button) {
      const item =
        button.closest(
          "[data-cinematic-item]"
        );

      const video =
        item?.querySelector(
          "[data-cinematic-video]"
        );

      if (!video) return;

      if (
        video.paused ||
        video.ended
      ) {
        this.playVideo(video);
      } else {
        this.pauseVideo(
          video,
          false
        );
      }

      this.syncMuteButtons();
    }

    toggleMute(button) {
      const item =
        button.closest(
          "[data-cinematic-item]"
        );

      const video =
        item?.querySelector(
          "[data-cinematic-video]"
        );

      if (!video) return;

      video.muted =
        !video.muted;

      this.updateMuteButton(
        button,
        video
      );
    }

    syncMuteButtons() {
      this.items.forEach(
        (item) => {
          const button =
            item.querySelector(
              "[data-cinematic-mute]"
            );

          const video =
            item.querySelector(
              "[data-cinematic-video]"
            );

          if (
            !button ||
            !video
          ) {
            return;
          }

          const isPlaying =
            !video.paused &&
            !video.ended;

          const shouldShow =
            this.enableVideoAutoplay
              ? item ===
                this.activeItem
              : isPlaying;

          button.classList.toggle(
            "is-visible",
            shouldShow
          );

          this.updateMuteButton(
            button,
            video
          );
        }
      );
    }

    updateMuteButton(
      button,
      video
    ) {
      button.setAttribute(
        "aria-label",
        video.muted
          ? "Muted"
          : "Unmuted"
      );

      button.setAttribute(
        "aria-pressed",
        video.muted
          ? "true"
          : "false"
      );
    }

    /*
    ==========================================================
    POPOVERS
    ==========================================================
    */

    getPopoverForOpener(opener) {
      if (!opener) return null;

      const item =
        opener.closest(
          "[data-cinematic-item]"
        );

      if (!item) return null;

      return item.querySelector(
        "[data-cinematic-popover]"
      );
    }

    openPopover(opener) {
      if (!opener) return;

      const popover =
        this.getPopoverForOpener(
          opener
        );

      if (!popover) return;

      this.closeAllPopovers();

      popover.hidden = false;

      opener.setAttribute(
        "aria-expanded",
        "true"
      );

      this.currentOpener =
        opener;

      this.focusFirst(popover);
    }

    closePopover(
      opener,
      returnFocus = true
    ) {
      if (!opener) return;

      const popover =
        this.getPopoverForOpener(
          opener
        );

      if (!popover) return;

      popover.hidden = true;

      opener.setAttribute(
        "aria-expanded",
        "false"
      );

      if (
        this.currentOpener ===
        opener
      ) {
        this.currentOpener = null;
      }

      if (returnFocus) {
        opener.focus({
          preventScroll: true,
        });
      }
    }

    togglePopover(opener) {
      const popover =
        this.getPopoverForOpener(
          opener
        );

      if (!popover) return;

      if (popover.hidden) {
        this.openPopover(opener);
      } else {
        this.closePopover(opener);
      }
    }

    closeAllPopovers() {
      this.querySelectorAll(
        "[data-cinematic-popover]"
      ).forEach((popover) => {
        popover.hidden = true;
      });

      this.querySelectorAll(
        "[data-cinematic-toggle]"
      ).forEach((toggle) => {
        toggle.setAttribute(
          "aria-expanded",
          "false"
        );
      });

      this.currentOpener = null;
    }

    focusFirst(container) {
      const focusable =
        Array.from(
          container.querySelectorAll(
            FOCUSABLE_SELECTOR
          )
        ).filter((element) => {
          return (
            !element.hasAttribute(
              "disabled"
            ) &&
            element.offsetParent !==
              null
          );
        });

      focusable[0]?.focus({
        preventScroll: true,
      });
    }

    /*
    ==========================================================
    ADD TO CART
    ==========================================================
    */

    async handleAddToCart(button) {
      const variantId =
        button.dataset.variantId;

      if (!variantId) return;

      const cart =
        document.querySelector(
          "cart-notification"
        ) ||
        document.querySelector(
          "cart-drawer"
        );

      const idle =
        button.querySelector(
          "[data-cinematic-add-idle]"
        );

      const previousText =
        idle?.textContent;

      button.classList.add(
        "is-loading"
      );

      button.setAttribute(
        "aria-busy",
        "true"
      );

      button.disabled = true;

      button.setAttribute(
        "aria-disabled",
        "true"
      );

      try {
        const config =
          getFetchConfig();

        const formData =
          new FormData();

        formData.append(
          "id",
          variantId
        );

        formData.append(
          "quantity",
          "1"
        );

        if (
          cart &&
          typeof cart.getSectionsToRender ===
            "function"
        ) {
          formData.append(
            "sections",
            cart
              .getSectionsToRender()
              .map(
                (section) =>
                  section.id
              )
              .join(",")
          );

          formData.append(
            "sections_url",
            window.location.pathname
          );

          if (
            typeof cart.setActiveElement ===
            "function"
          ) {
            cart.setActiveElement(
              button
            );
          }
        }

        config.body =
          formData;

        const response =
          await fetch(
            getCartAddUrl(),
            config
          );

        const data =
          await response.json();

        if (data.status) {
          if (
            typeof window.publish ===
              "function" &&
            typeof window.PUB_SUB_EVENTS !==
              "undefined"
          ) {
            window.publish(
              window.PUB_SUB_EVENTS
                .cartError,
              {
                source:
                  "cinematic-slider-portable",
                productVariantId:
                  variantId,
                errors:
                  data.errors ||
                  data.description,
                message:
                  data.message,
              }
            );
          }

          throw data;
        }

        if (idle) {
          idle.textContent =
            "Added";
        }

        this.closeAllPopovers();

        if (
          typeof window.publish ===
            "function" &&
          typeof window.PUB_SUB_EVENTS !==
            "undefined"
        ) {
          try {
            await window.publish(
              window.PUB_SUB_EVENTS
                .cartUpdate,
              {
                source:
                  "cinematic-slider-portable",
                productVariantId:
                  variantId,
                cartData:
                  data,
              }
            );
          } catch (error) {}
        }

        if (
          cart &&
          typeof cart.renderContents ===
            "function"
        ) {
          cart.renderContents(
            data
          );
        } else {
          await this.refreshCartCount();

          const openedCartUi =
            await this.openCartUi(
              button
            );

          if (!openedCartUi) {
            this.showFallbackCartConfirmation(
              button,
              data
            );
          }
        }

        window.setTimeout(
          () => {
            if (
              idle &&
              previousText
            ) {
              idle.textContent =
                previousText;
            }
          },
          1600
        );
      } catch (error) {
        document.dispatchEvent(
          new CustomEvent(
            "cart-error",
            {
              detail: {
                error,
              },
            }
          )
        );

        console.error(
          "Cinematic slider add to cart error:",
          error
        );
      } finally {
        button.classList.remove(
          "is-loading"
        );

        button.setAttribute(
          "aria-busy",
          "false"
        );

        button.disabled =
          false;

        button.setAttribute(
          "aria-disabled",
          "false"
        );
      }
    }

    /*
    ==========================================================
    CART HELPERS
    ==========================================================
    */

    getCartSections() {
      const sections = [];

      const drawer =
        document.getElementById(
          "Cart-Drawer"
        ) ||
        document.querySelector(
          "cart-drawer"
        );

      const bubble =
        document.getElementById(
          "Cart-Icon-Bubble"
        ) ||
        document.getElementById(
          "cart-icon-bubble"
        );

      if (bubble?.id) {
        sections.push({
          id: bubble.id,
          section:
            "cart-icon-bubble",
          selector:
            ".shopify-section",
        });
      }

      if (drawer?.id) {
        sections.push({
          id: drawer.id,
          section:
            drawer.dataset
              ?.sectionId ||
            "cart-drawer",
          selector:
            "[data-cart-drawer-content]",
        });
      }

      return sections;
    }

    renderCartSections(
      sections = {}
    ) {
      this.getCartSections().forEach(
        (section) => {
          const html =
            sections[
              section.section
            ];

          if (!html) return;

          const container =
            document.getElementById(
              section.id
            );

          if (!container) return;

          const parsed =
            new DOMParser().parseFromString(
              html,
              "text/html"
            );

          const element =
            section.selector
              ? parsed.querySelector(
                  section.selector
                )
              : parsed.body
                  .firstElementChild;

          if (!element) return;

          if (
            section.selector ===
            "[data-cart-drawer-content]"
          ) {
            const contentTarget =
              container.querySelector(
                "[data-cart-drawer-content]"
              );

            const summaryTarget =
              container.querySelector(
                "[data-cart-drawer-summary]"
              );

            const summarySource =
              parsed.querySelector(
                "[data-cart-drawer-summary]"
              );

            if (contentTarget) {
              contentTarget.innerHTML =
                element.innerHTML;
            }

            if (
              summaryTarget &&
              summarySource
            ) {
              summaryTarget.innerHTML =
                summarySource.innerHTML;

              summaryTarget.hidden =
                false;

              summaryTarget.classList.remove(
                "hidden"
              );
            }
          } else {
            container.innerHTML =
              element.innerHTML;
          }
        }
      );
    }

    async refreshCartCount() {
      const targets =
        document.querySelectorAll(
          "[data-cart-count], .cart-count-bubble span:first-child, #cart-icon-bubble span:first-child"
        );

      if (!targets.length) return;

      try {
        const response =
          await fetch(
            "/cart.js",
            {
              headers: {
                Accept:
                  "application/json",
              },
            }
          );

        const cart =
          await response.json();

        targets.forEach(
          (target) => {
            target.textContent =
              cart.item_count;
          }
        );
      } catch (error) {}
    }

    async openCartUi(opener) {
      const drawer =
        document.getElementById(
          "Cart-Drawer"
        ) ||
        document.querySelector(
          "cart-drawer"
        );

      const notification =
        document.querySelector(
          "cart-notification"
        );

      if (drawer) {
        if (
          typeof drawer.show ===
          "function"
        ) {
          drawer.show(opener);

          return true;
        }

        if (
          typeof drawer.open ===
          "function"
        ) {
          drawer.open(opener);

          return true;
        }

        drawer.setAttribute(
          "open",
          ""
        );

        drawer.classList.add(
          "is-active",
          "active"
        );

        return true;
      }

      if (
        notification &&
        typeof notification.open ===
          "function"
      ) {
        notification.open();

        return true;
      }

      return false;
    }

    showFallbackCartConfirmation(
      button,
      cartData
    ) {
      const item =
        button.closest(
          "[data-cinematic-item]"
        );

      if (!item) return;

      item
        .querySelector(
          "[data-cinematic-fallback-cart]"
        )
        ?.remove();

      const confirmation =
        document.createElement(
          "div"
        );

      confirmation.className =
        "cinematic-slider__fallback-cart";

      confirmation.setAttribute(
        "data-cinematic-fallback-cart",
        ""
      );

      confirmation.setAttribute(
        "role",
        "status"
      );

      confirmation.innerHTML = `
        <span class="cinematic-slider__fallback-cart-title">
          Producto agregado
        </span>

        <a
          class="cinematic-slider__fallback-cart-link"
          href="/cart"
        >
          Ver carrito
        </a>
      `;

      item.appendChild(
        confirmation
      );

      window.setTimeout(
        () => {
          confirmation.remove();
        },
        3200
      );
    }
  }

  customElements.define(
    "cinematic-slider-portable",
    CinematicSliderPortable
  );
})();